import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Search, X, Users } from 'lucide-react';
import { api } from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { toast } from 'react-toastify';
import { serviceWords } from '../utils/crewWork';
import { useReadOnly } from '../hooks/useReadOnly';
import AccountMark from '../components/AccountMark';

/**
 * Every client's yard on one map. Pins take the color of the route the
 * yard is on (grey = not on a route); tap a pin to see who it is, which route
 * and day, the price, and a link to the customer.
 *
 * Owner's asks (Sep 16 2026): pick several routes at once; cancelled yards
 * and "no route yet" yards each get their own tab; type any address to drop
 * a pin and see the nearest yards and the routes they are on.
 *
 * Client groups (owner, Oct 3 2026): "for the client map I want to be able to
 * make groups … I want to be able to look at not on routes and make sure none
 * of them should be and some of them are just landscaping clients." He makes
 * groups ("Landscaping only"), puts clients in them from the map, and the
 * No-route list shows only the real exceptions — the grouped ones sit under
 * their group. Groups and members come with /properties/map; writes go to
 * /client-groups (services/clientGroups on the server).
 */
const NO_ROUTE = '#9ca3af';
const CANCELLED = '#ef4444';
// The colors a new group can wear. Short on purpose — a dot has to be told apart at a glance.
const PALETTE = ['#84cc16', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#6b7280'];
function pinIcon(color: string, active: boolean) {
  return L.divIcon({
    className: '',
    html: `<div style="width:${active ? 18 : 14}px;height:${active ? 18 : 14}px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18], iconAnchor: [9, 9], popupAnchor: [0, -9],
  });
}
const SEARCH_ICON = L.divIcon({
  className: '',
  html: '<div style="width:22px;height:22px;border-radius:9999px 9999px 9999px 0;transform:rotate(-45deg);background:#111827;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>',
  iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -20],
});

function milesBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 3958.8, toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
const routeKey = (p: any) => (p.route_id ? `r${p.route_id}` : (p.day ? `d${p.day_of_week}` : 'none'));

const inGroup = (p: any, gid: number) => ((p.group_ids || []) as number[]).includes(gid);
type Group = { id: number; name: string; color: string | null; no_route_ok: number; count: number };
type Tab = 'routes' | 'noroute' | 'cancelled' | 'group';

/** "Mowing · Sep 12", "Landscaping project · Aug 3", or "no visits yet" — what helps him decide if a yard belongs on a route. */
function lastVisitWords(p: any): string {
  const lj = p?.last_job;
  if (!lj?.date) return 'no visits yet';
  const t = String(lj.service_type || '').toLowerCase();
  const what = /mow/.test(t) ? 'Mowing' : t === 'project' ? 'Landscaping project' : (serviceWords(t) || 'Visit');
  const [y, m, d] = String(lj.date).split('-').map(Number);
  const when = y && m && d ? new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : lj.date;
  return `${what} · ${when}`;
}

/**
 * The No-route list, split the same way the server's services/clientGroups.splitNoRoute does:
 * a yard with no route is an exception unless its customer is in a group that doesn't need one.
 */
function splitNoRoute(active: any[], groups: Group[]) {
  const okGroups = new Map(groups.filter(g => Number(g.no_route_ok) === 1).map(g => [g.id, g]));
  const exceptions: any[] = [];
  const by = new Map<number, { group: Group; properties: any[] }>();
  for (const p of active) {
    if (p.route_id) continue;
    const mine = ((p.group_ids || []) as number[]).map(id => okGroups.get(id)).filter(Boolean) as Group[];
    if (!mine.length) { exceptions.push(p); continue; }
    for (const g of mine) {
      const cur = by.get(g.id) || { group: g, properties: [] };
      cur.properties.push(p); by.set(g.id, cur);
    }
  }
  return { exceptions, explained: Array.from(by.values()).sort((a, b) => b.properties.length - a.properties.length) };
}

export default function ClientsMap() {
  const { data, isLoading, isError } = useQuery('clients-map', () => api.get('/properties/map').then(r => r.data), { staleTime: 60_000 });
  const qc = useQueryClient();
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const pinRest = useMutation(() => api.post('/properties/geocode-all', {}), {
    onSuccess: (r: any) => { const d = r.data || {}; toast.success(d.message || `Pinned ${d.geocoded ?? 0}${d.failed ? `, ${d.failed} still need a better address` : ''}`); qc.invalidateQueries('clients-map'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not pin those'); },
  });
  const [tab, setTab] = useState<Tab>('routes');
  const [selected, setSelected] = useState<Set<string>>(new Set());   // several routes at once; empty = every route
  const [groupSel, setGroupSel] = useState<number | null>(null);        // the group chip that is on
  const [panel, setPanel] = useState(false);                             // the Groups… panel
  // "New group…" from a pin: the panel opens and, once the group is made, that customer goes in it.
  const [pendingAdd, setPendingAdd] = useState<{ customer_id: number; name: string } | null>(null);
  // The Groups… panel's own fields.
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PALETTE[0]);
  const [newNoRoute, setNewNoRoute] = useState(true);
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [addr, setAddr] = useState('');
  const [found, setFound] = useState<{ lat: number; lng: number; label: string } | null>(null);
  // Find a client by name (owner's list, Oct 20 2026): type a name, pick them, the map goes to their pin and opens it.
  const [who, setWho] = useState('');
  const [picked, setPicked] = useState<any | null>(null);
  const [whoOpen, setWhoOpen] = useState(false);
  const whoBox = useRef<HTMLDivElement | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // The service area: a ring around every yard on a mowing schedule (Settings → Service area).
  const [rings, setRings] = useState(false);
  const { data: area } = useQuery('service-area', () => api.get('/service-area').then(r => r.data), { staleTime: 60_000, enabled: rings });

  const all: any[] = useMemo(() => data?.properties || [], [data]);
  const groups: Group[] = useMemo(() => data?.groups || [], [data]);
  const groupById = useMemo(() => new Map(groups.map(g => [g.id, g])), [groups]);
  const unpinned: any[] = useMemo(() => (data?.unpinned || []).filter((p: any) => p.service_status !== 'cancelled'), [data]);
  const active = useMemo(() => all.filter(p => p.service_status !== 'cancelled'), [all]);
  const cancelled = useMemo(() => all.filter(p => p.service_status === 'cancelled'), [all]);
  const { exceptions: noRoute, explained } = useMemo(() => splitNoRoute(active, groups), [active, groups]);
  const groupCounts = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of active) for (const id of (p.group_ids || []) as number[]) m.set(id, (m.get(id) || 0) + 1);
    return m;
  }, [active]);
  // The one-tap button on a No-route row: "Landscaping only" if it is still there, else the first group that doesn't need a route.
  const quickGroup = useMemo(() => groups.find(g => g.name.toLowerCase() === 'landscaping only' && Number(g.no_route_ok) === 1) || groups.find(g => Number(g.no_route_ok) === 1) || null, [groups]);
  const routes = useMemo(() => {
    const m = new Map<string, { key: string; name: string; color: string; day: string | null; count: number }>();
    for (const p of active) {
      if (!p.route_id && !p.day) continue;
      const key = routeKey(p);
      const cur = m.get(key) || { key, name: p.route_name || `${p.day}s (no route)`, color: p.route_color || '#6366f1', day: p.day, count: 0 };
      cur.count++; m.set(key, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [active]);

  const activeGroup = tab === 'group' && groupSel != null ? groupById.get(groupSel) || null : null;
  const groupYards = useMemo(() => (activeGroup ? active.filter(p => inGroup(p, activeGroup.id)) : []), [active, activeGroup]);
  const shown = tab === 'cancelled' ? cancelled
    : tab === 'noroute' ? noRoute
    : tab === 'group' ? groupYards
    : selected.size ? active.filter(p => selected.has(routeKey(p))) : active;
  const nearest = useMemo(() => {
    if (!found) return [];
    return active.map(p => ({ ...p, miles: milesBetween(found, p) })).sort((a, b) => a.miles - b.miles).slice(0, 8);
  }, [found, active]);

  // Every yard, pinned or not, cancelled or not — a name he types should find the person wherever they are.
  const everyone = useMemo(() => [...all, ...(data?.unpinned || [])], [all, data]);
  const whoMatches = useMemo(() => {
    const q = who.trim().toLowerCase();
    if (q.length < 2) return [];
    const words = q.split(/\s+/).filter(Boolean);
    const hay = (p: any) => `${p.customer_name || ''} ${p.address || ''} ${p.city || ''}`.toLowerCase();
    return everyone.filter(p => { const h = hay(p); return words.every(w => h.includes(w)); })
      .sort((a, b) => String(a.customer_name || '').localeCompare(String(b.customer_name || '')))
      .slice(0, 8);
  }, [who, everyone]);
  const pickedPinned = !!(picked && picked.lat != null && picked.lng != null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (whoBox.current && !whoBox.current.contains(e.target as Node)) setWhoOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const center: [number, number] = pickedPinned ? [Number(picked.lat), Number(picked.lng)] : found ? [found.lat, found.lng]
    : shown.length ? [shown.reduce((s, p) => s + p.lat, 0) / shown.length, shown.reduce((s, p) => s + p.lng, 0) / shown.length]
    : [30.577, -97.775];
  const zoom = pickedPinned ? 16 : found ? 13 : (tab !== 'routes' || selected.size) ? 12 : 11;
  const mapKey = `${tab}|${groupSel ?? ''}|${Array.from(selected).sort().join(',')}|${found ? `${found.lat},${found.lng}` : ''}|${pickedPinned ? `p${picked.property_id}` : ''}`;

  function toggleRoute(key: string) {
    setTab('routes');
    setSelected(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }
  function showGroup(id: number) { setTab('group'); setGroupSel(id); }
  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    const q = addr.trim();
    if (!q) return;
    setSearching(true); setSearchError(null);
    try {
      const r = await api.get('/properties/geocode', { params: { address: /tx\b|texas/i.test(q) ? q : `${q}, TX` } });
      if (!r.data?.found) { setSearchError(r.data?.message || 'Could not find that address'); setFound(null); return; }
      setFound({ lat: Number(r.data.lat), lng: Number(r.data.lng), label: r.data.display_name || q });
    } catch (err: any) { setSearchError(err.response?.data?.message || 'Could not find that address'); }
    finally { setSearching(false); }
  }

  // ── Group writes. Each one refreshes the map and says what it did in words. ──
  const oops = (e: any, fallback: string) => { toast.error(e?.response?.data?.message || fallback); };
  const members = useMutation(
    (v: { group: Group; customer_id: number; name: string; add: boolean }) =>
      api.put(`/client-groups/${v.group.id}/members`, v.add ? { add: [v.customer_id] } : { remove: [v.customer_id] }),
    {
      onSuccess: (_r, v) => { toast.success(v.add ? `${v.name} is in ${v.group.name} now` : `${v.name} is out of ${v.group.name}`); qc.invalidateQueries('clients-map'); qc.invalidateQueries('customer-groups'); },
      onError: (e: any) => oops(e, 'Could not change the group'),
    });
  const makeGroup = useMutation(
    (v: { name: string; color: string; no_route_ok: number }) => api.post('/client-groups', v).then(r => r.data.group as Group),
    {
      onSuccess: (g) => {
        qc.invalidateQueries('clients-map'); qc.invalidateQueries('customer-groups');
        if (pendingAdd) { members.mutate({ group: g, customer_id: pendingAdd.customer_id, name: pendingAdd.name, add: true }); setPendingAdd(null); }
        else toast.success(`Made the group "${g.name}"`);
        setNewName(''); setNewColor(PALETTE[(groups.length + 1) % PALETTE.length]); setNewNoRoute(true);
      },
      onError: (e: any) => oops(e, 'Could not make the group'),
    });
  const saveGroup = useMutation(
    (v: { id: number; name?: string; color?: string | null; no_route_ok?: number }) => api.put(`/client-groups/${v.id}`, v),
    { onSuccess: () => { qc.invalidateQueries('clients-map'); qc.invalidateQueries('customer-groups'); setRenaming(null); }, onError: (e: any) => oops(e, 'Could not save the group') });
  const dropGroup = useMutation((g: Group) => api.delete(`/client-groups/${g.id}`), {
    onSuccess: (r: any, g) => { toast.success(r.data?.message || `Deleted "${g.name}"`); qc.invalidateQueries('clients-map'); qc.invalidateQueries('customer-groups'); if (groupSel === g.id) { setTab('routes'); setGroupSel(null); } },
    onError: (e: any) => oops(e, 'Could not delete the group'),
  });
  function confirmDelete(g: Group) {
    // The server's count is customers in the group; groupCounts is yards on the map (one person can own two).
    const n = Number(g.count) || 0;
    if (window.confirm(`Delete '${g.name}'? The ${n} client${n === 1 ? '' : 's'} in it stay customers; they just leave the group.`)) dropGroup.mutate(g);
  }
  // The "Add to group ▾" select on a pin and on the customer rows.
  function pickGroup(p: any, value: string) {
    if (!value) return;
    if (value === 'new') { setPendingAdd({ customer_id: p.customer_id, name: p.customer_name }); setPanel(true); return; }
    const g = groupById.get(Number(value));
    if (g) members.mutate({ group: g, customer_id: p.customer_id, name: p.customer_name, add: true });
  }
  // One tap puts a No-route yard's customer in "Landscaping only" (or the first group that doesn't need a route).
  function quickAdd(p: any) {
    if (quickGroup) members.mutate({ group: quickGroup, customer_id: p.customer_id, name: p.customer_name, add: true });
    else { setPendingAdd({ customer_id: p.customer_id, name: p.customer_name }); setPanel(true); }
  }

  const chip = (on: boolean) => `text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`;
  const groupsOf = (p: any): Group[] => ((p.group_ids || []) as number[]).map(id => groupById.get(id)).filter(Boolean) as Group[];
  const money = (p: any) => (p.mowing_price != null ? `$${Number(p.mowing_price).toFixed(0)} a mow` : null);

  /** The facts and quick actions on one yard row (the No-route list and a group's list). */
  function yardRow(p: any, mode: 'noroute' | 'group') {
    const mine = groupsOf(p);
    return (
      <li key={p.property_id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: mode === 'group' && activeGroup ? (activeGroup.color || NO_ROUTE) : (p.route_color || NO_ROUTE) }} />
        <Link to={`/customers?profile=${p.customer_id}`} className="font-medium text-gray-900 hover:underline">{p.customer_name}<AccountMark mark={p.account_mark} /></Link>
        <span className="text-xs text-gray-500 truncate">{[p.address, p.city].filter(Boolean).join(', ')}</span>
        <span className="text-xs text-gray-600">last visit: {lastVisitWords(p)}</span>
        {money(p) && <span className="text-xs text-gray-600">{money(p)}</span>}
        {mode === 'group' && p.route_name && <span className="text-xs text-gray-500">on {p.route_name}</span>}
        {mode === 'noroute' && mine.length > 0 && <span className="text-xs text-gray-500">in {mine.map(g => g.name).join(', ')}</span>}
        <span className="ml-auto flex items-center gap-2">
          {mode === 'noroute' && (
            <button onClick={() => quickAdd(p)} disabled={readOnly || members.isLoading}
              className="text-xs px-2 py-0.5 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
              title={quickGroup ? `Put ${p.customer_name} in ${quickGroup.name}` : 'Make a group for them'}>
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: quickGroup?.color || NO_ROUTE }} />{quickGroup ? quickGroup.name : 'Put in a group…'}
            </button>
          )}
          {mode === 'group' && activeGroup && (
            <button onClick={() => members.mutate({ group: activeGroup, customer_id: p.customer_id, name: p.customer_name, add: false })} disabled={readOnly || members.isLoading}
              className="text-xs px-2 py-0.5 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">Remove from group</button>
          )}
          <Link to={`/customers?profile=${p.customer_id}`} className="text-xs text-brand-700 font-medium underline">Open customer</Link>
        </span>
      </li>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Client map</h1>
          <p className="text-xs text-gray-500">{active.length} yards pinned{unpinned.length ? ` · ${unpinned.length} not on the map yet` : ''} — tap a pin to see the route</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-start gap-1.5 w-full sm:w-auto">
        {/* Find a client by name: the matches drop down as he types; picking one flies to their pin and opens it. */}
        <div ref={whoBox} className="relative flex-1 sm:w-64">
          <div className="relative">
            <Users className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={who} onChange={e => { setWho(e.target.value); setWhoOpen(true); if (picked) setPicked(null); }} onFocus={() => setWhoOpen(true)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (whoMatches[0]) { setPicked(whoMatches[0]); setWho(whoMatches[0].customer_name || ''); setWhoOpen(false); } } if (e.key === 'Escape') setWhoOpen(false); }}
              placeholder="Find a client by name" aria-label="Find a client by name" className="input text-sm py-1.5 w-full pl-8 pr-7" />
            {(who || picked) && <button type="button" onClick={() => { setWho(''); setPicked(null); setWhoOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" title="Clear"><X className="h-4 w-4" /></button>}
          </div>
          {whoOpen && who.trim().length >= 2 && (
            <ul className="absolute z-[1000] mt-1 w-full max-h-72 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg text-sm divide-y divide-gray-100">
              {whoMatches.map(p => (
                <li key={p.property_id}>
                  <button type="button" onClick={() => { setPicked(p); setWho(p.customer_name || ''); setWhoOpen(false); }} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: p.service_status === 'cancelled' ? CANCELLED : (p.route_color || NO_ROUTE) }} />
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900 truncate">{p.customer_name}<AccountMark mark={p.account_mark} /></span>
                      <span className="block text-xs text-gray-500 truncate">{[p.address, p.city].filter(Boolean).join(', ') || 'no address'}{p.route_name ? ` · ${p.route_name}` : ''}{p.service_status === 'cancelled' ? ' · cancelled' : ''}{p.lat == null ? ' · not on the map yet' : ''}</span>
                    </span>
                  </button>
                </li>
              ))}
              {whoMatches.length === 0 && <li className="px-3 py-2 text-xs text-gray-500">No client matches "{who.trim()}".</li>}
            </ul>
          )}
        </div>
        {/* Type any address: a pin drops and the nearest yards list underneath. */}
        {/* The same address box as the intake forms: pick a match and the pin drops at once; "Find" still looks up whatever was typed. */}
        <form onSubmit={search} className="flex items-start gap-1.5 w-full sm:w-auto">
          <div className="flex-1 sm:w-80">
            <AddressAutocomplete value={addr} onChange={setAddr} placeholder="Type an address to see the nearest yards" className="input text-sm py-1.5 w-full"
              onSelect={s => {
                const label = s.label || [s.address, s.city].filter(Boolean).join(', ');
                setAddr(label); setSearchError(null);
                if (s.lat != null && s.lng != null) setFound({ lat: Number(s.lat), lng: Number(s.lng), label });
              }} />
          </div>
          {found && <button type="button" onClick={() => { setFound(null); setAddr(''); setSearchError(null); }} className="mt-1.5 text-gray-500 hover:text-gray-700" title="Clear"><X className="h-4 w-4" /></button>}
          <button type="submit" disabled={searching || !addr.trim()} className="btn-primary text-sm py-1.5 flex items-center gap-1 disabled:opacity-50"><Search className="h-3.5 w-3.5" /> {searching ? 'Finding…' : 'Find'}</button>
        </form>
        </div>
      </div>

      {/* The client he picked by name: where they are, and the way to them. */}
      {picked && (
        <div className="card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-l-4" style={{ borderLeftColor: picked.service_status === 'cancelled' ? CANCELLED : (picked.route_color || NO_ROUTE) }}>
          <Users className="h-4 w-4 text-gray-500" />
          <Link to={`/customers?profile=${picked.customer_id}`} className="font-semibold text-gray-900 hover:underline">{picked.customer_name}<AccountMark mark={picked.account_mark} /></Link>
          <span className="text-xs text-gray-600">{[picked.address, picked.city].filter(Boolean).join(', ') || 'no address'}</span>
          <span className="text-xs text-gray-600">{picked.route_name ? `${picked.route_name}${picked.day ? ` · ${picked.day}s` : ''}` : picked.day ? `${picked.day}s · no route yet` : 'not on a mowing route'}{picked.service_status === 'cancelled' ? ' · cancelled' : ''}</span>
          {money(picked) && <span className="text-xs text-gray-600">{money(picked)}</span>}
          <span className="text-xs text-gray-600">last visit: {lastVisitWords(picked)}</span>
          {!pickedPinned && <span className="text-xs text-amber-800">Not on the map yet — open the customer and check the address, or tap "Pin them" below.</span>}
          <span className="ml-auto flex items-center gap-2">
            <Link to={`/customers?profile=${picked.customer_id}`} className="text-xs text-brand-700 font-medium underline">Open customer</Link>
            <button type="button" onClick={() => { setWho(''); setPicked(null); }} className="text-gray-500 hover:text-gray-700" title="Clear"><X className="h-4 w-4" /></button>
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={() => { setTab('routes'); setSelected(new Set()); }} className={chip(tab === 'routes' && selected.size === 0)}>All routes · {active.length}</button>
        {routes.map(r => (
          <button key={r.key} onClick={() => toggleRoute(r.key)} title={r.day ? `${r.day}s` : ''} className={chip(tab === 'routes' && selected.has(r.key))}>
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: r.color }} />{r.name} · {r.count}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-gray-300" />
        <button onClick={() => setTab('noroute')} className={chip(tab === 'noroute')} title={explained.length ? `${explained.reduce((s, e) => s + e.properties.length, 0)} more are in a group that doesn't need a route` : ''}>
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: NO_ROUTE }} />No route yet · {noRoute.length}
        </button>
        {/* His groups: one chip each, then the panel that makes and changes them. */}
        {groups.length > 0 && <span className="mx-1 h-4 w-px bg-gray-300" />}
        {groups.map(g => (
          <button key={g.id} onClick={() => showGroup(g.id)} className={chip(tab === 'group' && groupSel === g.id)} title={Number(g.no_route_ok) === 1 ? "These clients don't need a mowing route" : ''}>
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: g.color || NO_ROUTE }} />{g.name} · {groupCounts.get(g.id) || 0}
          </button>
        ))}
        <button onClick={() => setPanel(v => !v)} className={chip(panel)} title="Make a group, rename one, or delete one"><Users className="h-3 w-3" />Groups…</button>
        <button onClick={() => setRings(r => !r)} className={chip(rings)} title="Yards inside a ring pay no travel charge">
          <span className="inline-block h-2.5 w-2.5 rounded-full border-2 border-emerald-500" />Service area{area?.settings ? ` · ${area.settings.service_area_miles} mi` : ''}
        </button>
        <button onClick={() => setTab('cancelled')} className={chip(tab === 'cancelled')}><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: CANCELLED }} />Cancelled · {cancelled.length}</button>
        {tab === 'routes' && selected.size > 1 && <span className="text-xs text-gray-500 ml-1">{selected.size} routes shown — tap a chip to add or drop one</span>}
      </div>
      {searchError && <p className="text-sm text-red-600">{searchError}</p>}

      {panel && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Client groups</p>
            <button onClick={() => { setPanel(false); setPendingAdd(null); }} className="text-gray-500 hover:text-gray-700" title="Close"><X className="h-4 w-4" /></button>
          </div>
          <p className="text-xs text-gray-500">A group is just a name on some clients. Tick "don't need a mowing route" and the No-route list stops asking about them.</p>
          {pendingAdd && <p className="text-xs text-amber-700">Make the group and {pendingAdd.name} goes in it.</p>}
          <form onSubmit={e => { e.preventDefault(); if (newName.trim()) makeGroup.mutate({ name: newName.trim(), color: newColor, no_route_ok: newNoRoute ? 1 : 0 }); }} className="flex flex-wrap items-center gap-2">
            <input value={newName} onChange={e => setNewName(e.target.value)} maxLength={40} placeholder="New group name" className="input text-sm py-1.5 w-48" disabled={readOnly} />
            <span className="flex items-center gap-1">
              {PALETTE.map(c => (
                <button type="button" key={c} onClick={() => setNewColor(c)} title={c} className={`h-5 w-5 rounded-full border-2 ${newColor === c ? 'border-gray-900' : 'border-white'}`} style={{ background: c, boxShadow: '0 0 0 1px #d1d5db' }} />
              ))}
            </span>
            <label className="text-xs text-gray-700 flex items-center gap-1"><input type="checkbox" checked={newNoRoute} onChange={e => setNewNoRoute(e.target.checked)} /> these clients don't need a mowing route</label>
            <button type="submit" disabled={readOnly || makeGroup.isLoading || !newName.trim()} className="btn-primary text-xs py-1.5 disabled:opacity-50">{makeGroup.isLoading ? 'Making…' : 'Make the group'}</button>
          </form>
          {groups.length > 0 && (
            <ul className="text-sm divide-y divide-gray-100">
              {groups.map(g => (
                <li key={g.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: g.color || NO_ROUTE }} />
                  {renaming?.id === g.id ? (
                    <form onSubmit={e => { e.preventDefault(); if (renaming.name.trim()) saveGroup.mutate({ id: g.id, name: renaming.name.trim() }); }} className="flex items-center gap-1">
                      <input autoFocus value={renaming.name} onChange={e => setRenaming({ id: g.id, name: e.target.value })} maxLength={40} className="input text-sm py-1 w-44" />
                      <button type="submit" className="text-xs text-brand-700 font-medium">Save</button>
                      <button type="button" onClick={() => setRenaming(null)} className="text-xs text-gray-500">Cancel</button>
                    </form>
                  ) : (
                    <>
                      <button onClick={() => showGroup(g.id)} className="font-medium text-gray-900 hover:underline">{g.name}</button>
                      <span className="text-xs text-gray-500">{Number(g.count) || 0} client{Number(g.count) === 1 ? '' : 's'}</span>
                    </>
                  )}
                  <label className="text-xs text-gray-700 flex items-center gap-1">
                    <input type="checkbox" checked={Number(g.no_route_ok) === 1} disabled={readOnly || saveGroup.isLoading} onChange={e => saveGroup.mutate({ id: g.id, no_route_ok: e.target.checked ? 1 : 0 })} /> don't need a mowing route
                  </label>
                  <span className="flex items-center gap-1">
                    {PALETTE.map(c => (
                      <button type="button" key={c} disabled={readOnly} onClick={() => saveGroup.mutate({ id: g.id, color: c })} title="Color" className={`h-3.5 w-3.5 rounded-full border ${g.color === c ? 'border-gray-900' : 'border-white'}`} style={{ background: c, boxShadow: '0 0 0 1px #d1d5db' }} />
                    ))}
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    {renaming?.id !== g.id && <button onClick={() => setRenaming({ id: g.id, name: g.name })} disabled={readOnly} className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50">Rename</button>}
                    <button onClick={() => confirmDelete(g)} disabled={readOnly || dropGroup.isLoading} className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50">Delete</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Loading the map…</p>}
      {isError && <p className="text-sm text-red-600">Could not load the map.</p>}
      {data && (
        <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: found ? 'calc(100vh - 460px)' : 'calc(100vh - 260px)', minHeight: 360 }}>
          <MapContainer key={mapKey} center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {(pickedPinned && !shown.some(p => p.property_id === picked.property_id) ? [...shown, picked] : shown).map(p => {
              const mine = groupsOf(p);
              const notIn = groups.filter(g => !inGroup(p, g.id));
              const isPicked = pickedPinned && p.property_id === picked.property_id;
              return (
              <Marker key={p.property_id} position={[p.lat, p.lng]} icon={pinIcon(p.service_status === 'cancelled' ? CANCELLED : activeGroup ? (activeGroup.color || NO_ROUTE) : (p.route_color || (p.day ? '#6366f1' : NO_ROUTE)), isPicked || tab !== 'routes' || selected.size > 0)}
                eventHandlers={isPicked ? { add: (e: any) => { try { e.target.openPopup(); } catch { /* the map is still drawing */ } } } : undefined}>
                <Popup>
                  <div className="text-sm space-y-0.5 min-w-[200px]">
                    <p className="font-semibold text-gray-900">{p.customer_name}<AccountMark mark={p.account_mark} /></p>
                    <p className="text-xs text-gray-600">{[p.address, p.city].filter(Boolean).join(', ')}</p>
                    <p className="text-xs">
                      {p.route_name
                        ? <><span className="inline-block h-2 w-2 rounded-full mr-1 align-middle" style={{ background: p.route_color || NO_ROUTE }} /><span className="font-medium">{p.route_name}</span>{p.day ? ` · ${p.day}s` : ''}{p.cadence ? ` · ${p.cadence}` : ''}</>
                        : p.day ? <>{p.day}s{p.cadence ? ` · ${p.cadence}` : ''} · no route yet</>
                        : <span className="text-gray-500">Not on a mowing route</span>}
                    </p>
                    {p.mowing_price != null && <p className="text-xs text-gray-600">${Number(p.mowing_price).toFixed(0)} a mow{p.service_status === 'cancelled' ? ' · cancelled' : ''}</p>}
                    <p className="text-xs text-gray-600">last visit: {lastVisitWords(p)}</p>
                    {found && <p className="text-xs text-gray-500">{milesBetween(found, p).toFixed(1)} mi from the address you typed</p>}
                    {/* Their groups, each with its own × ; the select puts them in another or starts a new one. */}
                    <p className="text-xs flex flex-wrap items-center gap-1">
                      <span className="text-gray-500">Groups:</span>
                      {mine.length === 0 && <span className="text-gray-500">none</span>}
                      {mine.map(g => (
                        <span key={g.id} className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-1.5 py-0.5">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: g.color || NO_ROUTE }} />{g.name}
                          <button type="button" disabled={readOnly || members.isLoading} onClick={() => members.mutate({ group: g, customer_id: p.customer_id, name: p.customer_name, add: false })} title={`Take ${p.customer_name} out of ${g.name}`} className="text-gray-400 hover:text-gray-700 disabled:opacity-50">×</button>
                        </span>
                      ))}
                      <select value="" disabled={readOnly || members.isLoading} onChange={e => pickGroup(p, e.target.value)} className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white" title="Put this customer in a group">
                        <option value="">Add to group ▾</option>
                        {notIn.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        <option value="new">New group…</option>
                      </select>
                    </p>
                    <p className="text-xs pt-1">
                      <Link to={`/customers?profile=${p.customer_id}`} className="text-brand-700 font-medium underline">Open customer</Link>
                      {p.route_id && <> · <Link to={`/routes`} className="text-brand-700 font-medium underline">Routes</Link></>}
                    </p>
                  </div>
                </Popup>
              </Marker>
              );
            })}
            {rings && (area?.yards || []).map((y: any) => (
              <Circle key={`ring-${y.id}`} center={[y.lat, y.lng]} radius={(Number(area?.settings?.service_area_miles) || 10) * 1609.34}
                pathOptions={{ color: '#10b981', weight: 1, opacity: 0.35, fillColor: '#10b981', fillOpacity: 0.06 }} />
            ))}
            {rings && area?.home && (
              <Marker position={[area.home.lat, area.home.lng]} icon={SEARCH_ICON}>
                <Popup><div className="text-sm"><p className="font-semibold text-gray-900">{area.home.label || 'Home base'}</p><p className="text-xs text-gray-600">{area.home.address ? `${area.home.address} — ` : ''}where the truck starts and ends the day.</p></div></Popup>
              </Marker>
            )}
            {found && (
              <Marker position={[found.lat, found.lng]} icon={SEARCH_ICON}>
                <Popup><div className="text-sm"><p className="font-semibold text-gray-900">The address you typed</p><p className="text-xs text-gray-600">{found.label}</p></div></Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
      )}

      {/* The No-route list: only the yards nobody has explained yet. Each row carries what he needs to decide. */}
      {data && tab === 'noroute' && (
        <div className="card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> No route yet · {noRoute.length}</p>
          <p className="text-xs text-gray-500 mb-2">Active yards with no mowing route. If one is just a landscaping client, tap the group button and it leaves this list.</p>
          <ul className="text-sm divide-y divide-gray-100">
            {noRoute.map(p => yardRow(p, 'noroute'))}
            {noRoute.length === 0 && <li className="py-1.5 text-xs text-gray-500">Every active yard is on a route or in a group that doesn't need one.</li>}
          </ul>
          {explained.length > 0 && (
            <p className="text-xs text-gray-500 mt-3 flex flex-wrap items-center gap-1">
              Not on a route but in a group that doesn't need one:
              {explained.map((e, i) => (
                <React.Fragment key={e.group.id}>
                  {i > 0 && <span>·</span>}
                  <button onClick={() => showGroup(e.group.id)} className="text-gray-700 font-medium hover:underline flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: e.group.color || NO_ROUTE }} />{e.group.name} {e.properties.length}
                  </button>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      )}

      {/* A group's own list: the same facts, and a way out of the group. */}
      {data && activeGroup && (
        <div className="card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: activeGroup.color || NO_ROUTE }} /> {activeGroup.name} · {groupYards.length}
            {Number(activeGroup.no_route_ok) === 1 && <span className="normal-case font-normal tracking-normal text-gray-400">— these clients don't need a mowing route</span>}
          </p>
          <ul className="text-sm divide-y divide-gray-100">
            {groupYards.map(p => yardRow(p, 'group'))}
            {groupYards.length === 0 && <li className="py-1.5 text-xs text-gray-500">Nobody in this group yet — tap a pin and use "Add to group".</li>}
          </ul>
        </div>
      )}

      {found && (
        <div className="card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Nearest yards to {found.label}</p>
          <ul className="text-sm divide-y divide-gray-100">
            {nearest.map(p => (
              <li key={p.property_id} className="py-1.5 flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: p.route_color || NO_ROUTE }} />
                <span className="font-semibold text-gray-900 tabular-nums w-14">{p.miles.toFixed(1)} mi</span>
                <Link to={`/customers?profile=${p.customer_id}`} className="font-medium text-gray-900 hover:underline truncate">{p.customer_name}<AccountMark mark={p.account_mark} /></Link>
                <span className="text-xs text-gray-500 truncate hidden sm:inline">{[p.address, p.city].filter(Boolean).join(', ')}</span>
                <span className="ml-auto text-xs text-gray-600 whitespace-nowrap">{p.route_name || (p.day ? `${p.day}s · no route yet` : 'no route')}</span>
              </li>
            ))}
            {nearest.length === 0 && <li className="py-1.5 text-xs text-gray-500">No pinned yards yet.</li>}
          </ul>
        </div>
      )}

      {unpinned.length > 0 && tab !== 'cancelled' && (
        <div className="card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Not on the map yet · {unpinned.length}</p>
          <p className="text-xs text-gray-500 mb-2">These yards have no pin yet.</p>
          <button onClick={() => pinRest.mutate()} disabled={pinRest.isLoading} className="btn-primary text-xs py-1.5 mb-3 disabled:opacity-50">{pinRest.isLoading ? 'Pinning…' : 'Pin them from their addresses'}</button>
          <p className="text-xs text-gray-500 mb-2">If one still won't pin, its address is wrong or too new for the map — open the customer and fix it.</p>
          <ul className="text-sm divide-y divide-gray-100">
            {unpinned.map(p => (
              <li key={p.property_id} className="py-1.5 flex items-center gap-2">
                <Link to={`/customers?profile=${p.customer_id}`} className="font-medium text-gray-900 hover:underline">{p.customer_name}<AccountMark mark={p.account_mark} /></Link>
                <span className="text-xs text-gray-500 truncate">{[p.address, p.city].filter(Boolean).join(', ') || 'no address'}</span>
                {p.route_name && <span className="ml-auto text-xs text-gray-500">{p.route_name}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
